import { createIntl, createIntlCache } from '@formatjs/intl';
import Big from 'big.js';

const cache = createIntlCache();
const intl = createIntl(
  {
    locale: 'en-US',
    messages: {
      'components.transaction_status': 'Success',
    },
  },
  cache
);

export const formatNumberUtil = (
  value: number | string,
  {
    precision,
    notation,
    compactDisplay,
  }: {
    precision?: { min: number; max: number };
    notation?: 'compact' | 'standard';
    compactDisplay?: 'short' | 'long';
  } = {}
): string => {
  return intl.formatNumber(value as number, {
    minimumFractionDigits: precision?.min || 0,
    maximumFractionDigits: precision?.max || 0,
    notation,
    compactDisplay,
  });
};

export const tokenDivider = (decimals: number | null) => Big(10).pow(decimals || 0);

export const divideCEP18Balance = (balance: string | null, decimals: number | null): string | null => {
  if (balance == null) {
    return null;
  }

  return Big(balance).div(tokenDivider(decimals)).toFixed();
};

export const convertAmountToRaw = (amount: string, decimals: number): string => {
  if (!amount || amount === '0') return '0';

  try {
    return Big(amount).mul(tokenDivider(decimals)).toFixed();
  } catch (error) {
    console.error('Error converting amount to raw:', error);

    return '0';
  }
};

export const CSPRtoMotes = (cspr: string): string => {
  return Big(cspr).mul('1000000000').toFixed();
};

/**
 * Format token amount for display, removing insignificant trailing decimals
 * @param amount - Amount to format
 * @param maxDecimals - Maximum number of decimal places (default: 6)
 * @param minDecimals - Minimum number of decimal places (default: 2)
 */
export const formatTokenAmount = (
  amount: string | number,
  maxDecimals: number = 6,
  minDecimals: number = 2
): string => {
  try {
    const num = new Big(amount);

    // If amount is zero, return "0"
    if (num.eq(0)) {
      return '0';
    }

    // Format with max decimals
    let formatted = num.toFixed(maxDecimals);

    // Remove trailing zeros after decimal point
    formatted = formatted.replace(/(\.\d*?)0+$/, '$1');

    // Remove decimal point if no decimals remain
    formatted = formatted.replace(/\.$/, '');

    // Ensure minimum decimals for consistency
    const parts = formatted.split('.');
    if (parts.length === 1) {
      // No decimals, add minDecimals
      formatted = `${formatted}.${'0'.repeat(minDecimals)}`;
    } else if (parts[1].length < minDecimals) {
      // Less than minDecimals, pad with zeros
      formatted = `${parts[0]}.${parts[1].padEnd(minDecimals, '0')}`;
    }

    return formatted;
  } catch (error) {
    console.error('Error formatting token amount:', error);

    return '0';
  }
};
